import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { getShipmentVehicleOptions, resetShipmentVehicleOptions, setShipmentActionData, updateShipmentVehicle } from "../../../features/loadList/loadListSlice";
import { showModal } from "../../../features/modal/modalSlice";
import { useScrollLock } from "../../../hooks/useScrollLock";
import Btn from "../../shared/button/Btn";
import SmallSpinner from "../../shared/spinners/SmallSpinner";

const toArray = (value) => Array.isArray(value) ? value : Array.isArray(value?.$values) ? value.$values : [];
const hasValue = (value) => value !== null && value !== undefined && value !== "";
const sameId = (left, right) => `${left ?? ""}` === `${right ?? ""}`;

const Container = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, .6);

  .modal { width: min(640px, 100%); max-height: calc(100vh - 40px); overflow: auto; padding: 30px; border-radius: 12px; background: ${({theme}) => theme.mainBgColor}; color: ${({theme}) => theme.mainTextColor}; }
  h2 { margin: 0 0 8px; font-size: var(--font-size-md); }
  .current { margin-bottom: 22px; font-size: var(--font-size-sm); line-height: 1.6; opacity: .8; }
  .fields { display: flex; flex-direction: column; gap: 18px; }
  label { display: flex; flex-direction: column; gap: 7px; font-size: var(--font-size-sm); font-weight: var(--font-weight-lg); }
  select { width: 100%; min-height: 42px; padding: 0 10px; border: 1px solid ${({theme}) => theme.mainBorderColor}; border-radius: 6px; color: ${({theme}) => theme.mainTextColor}; background: ${({theme}) => theme.mainBgColor}; }
  option:disabled { color: #999; }
  .message { margin-top: 16px; color: #E85446; font-size: var(--font-size-sm); }
  .actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 26px; }
  @media (max-width: 520px) { .actions { flex-direction: column-reverse; } .actions button { width: 100%; } }
`;

const UpdateShipmentVehicleModal = () => {
  const dispatch = useDispatch();
  const {lockScroll, unlockScroll} = useScrollLock();
  const [driverId, setDriverId] = useState("");
  const [truckId, setTruckId] = useState("");
  const [trailerId, setTrailerId] = useState("");
  const [validationMessage, setValidationMessage] = useState("");
  const initialSelectionRef = useRef(null);
  const {shipmentActionData, shipmentVehicleOptions, shipmentVehicleOptionsError, isLoadingShipmentVehicleOptions, isLoadingUpdateShipmentVehicle} = useSelector(state => state.loadList);

  useEffect(() => {
    lockScroll();
    if (shipmentActionData?.id) dispatch(getShipmentVehicleOptions(shipmentActionData.id));
    return () => {
      unlockScroll();
      dispatch(resetShipmentVehicleOptions());
    };
  }, [dispatch, lockScroll, unlockScroll, shipmentActionData?.id]);

  const drivers = useMemo(() => {
    const rawDrivers = toArray(shipmentVehicleOptions?.drivers ?? shipmentVehicleOptions?.driverOptions);
    return rawDrivers.map(driver => ({
      ...driver,
      optionId: driver?.driverDevamliAraclarId ?? driver?.id ?? driver?.devamliAraclarId,
      optionName: driver?.driverName ?? driver?.name ?? driver?.userName ?? "İsimsiz sürücü",
      userId: driver?.driverUserId ?? driver?.userId
    }));
  }, [shipmentVehicleOptions]);

  const trucks = useMemo(() => toArray(shipmentVehicleOptions?.trucks ?? shipmentVehicleOptions?.truckOptions).map(truck => ({
    ...truck,
    optionId: truck?.truckId ?? truck?.id,
    optionName: truck?.truckPlateNumber ?? truck?.plateNumber ?? truck?.truckPlate ?? "Plakasız çekici"
  })), [shipmentVehicleOptions]);

  const trailers = useMemo(() => toArray(shipmentVehicleOptions?.trailers ?? shipmentVehicleOptions?.trailerOptions).map(trailer => ({
    ...trailer,
    optionId: trailer?.trailerId ?? trailer?.id,
    optionName: trailer?.trailerPlateNumber ?? trailer?.plateNumber ?? trailer?.trailerPlate ?? "Plakasız dorse"
  })), [shipmentVehicleOptions]);

  useEffect(() => {
    if (!shipmentVehicleOptions || initialSelectionRef.current) return;

    const currentDriverUserId = shipmentVehicleOptions.currentDriverUserId;
    const currentTruckId = shipmentVehicleOptions.currentTruckId;
    const currentTrailerId = shipmentVehicleOptions.currentTrailerId;
    const currentDriver = drivers.find(driver => driver.isCurrent) ??
      drivers.find(driver => sameId(driver.userId, currentDriverUserId));
    const currentTruck = trucks.find(truck => truck.isCurrent) ??
      trucks.find(truck => sameId(truck.optionId, currentTruckId));
    const currentTrailer = trailers.find(trailer => trailer.isCurrent) ??
      trailers.find(trailer => sameId(trailer.optionId, currentTrailerId));
    const initialSelection = {
      driverId: currentDriver?.optionId ?? "",
      truckId: currentTruck?.optionId ?? "",
      trailerId: currentTrailer?.optionId ?? "",
    };

    initialSelectionRef.current = initialSelection;
    setDriverId(initialSelection.driverId);
    setTruckId(initialSelection.truckId);
    setTrailerId(initialSelection.trailerId);
  }, [drivers, shipmentVehicleOptions, trailers, trucks]);

  const close = () => {
    if (isLoadingUpdateShipmentVehicle) return;
    dispatch(showModal(null));
    dispatch(setShipmentActionData(null));
    dispatch(resetShipmentVehicleOptions());
  };

  const submit = () => {
    const initialSelection = initialSelectionRef.current ?? {};
    const driverChanged = !sameId(driverId, initialSelection.driverId);
    const truckChanged = !sameId(truckId, initialSelection.truckId);
    const trailerChanged = !sameId(trailerId, initialSelection.trailerId);

    if (!driverChanged && !truckChanged && !trailerChanged) {
      setValidationMessage("Güncellemek için mevcut seçimlerden en az birini değiştirin.");
      return;
    }
    const selectedDriver = drivers.find(driver => String(driver.optionId) === String(driverId));
    const selectedTruck = trucks.find(truck => sameId(truck.optionId, truckId));
    const selectedTrailer = trailers.find(trailer => sameId(trailer.optionId, trailerId));
    dispatch(updateShipmentVehicle({
      shipmentId: shipmentActionData.id,
      ...(driverChanged && hasValue(driverId) ? {driverDevamliAraclarId: driverId} : {}),
      ...(truckChanged && hasValue(truckId) ? {truckId} : {}),
      ...(trailerChanged && hasValue(trailerId) ? {trailerId} : {}),
      viewData: {
        ...(driverChanged && selectedDriver ? {driverName: selectedDriver.optionName, driverPhoneNumber: selectedDriver.phoneNumber ?? selectedDriver.driverPhoneNumber} : {}),
        ...(truckChanged && selectedTruck ? {truckPlateNumber: selectedTruck.optionName} : {}),
        ...(trailerChanged && selectedTrailer ? {trailerPlateNumber: selectedTrailer.optionName} : {})
      }
    }));
  };

  return (
    <Container onClick={close}>
      <div className="modal" onClick={(event) => event.stopPropagation()}>
        <h2>Sürücü ve Araç Değiştir</h2>
        <div className="current">Sevkiyat ID: {shipmentActionData?.id}<br/>Mevcut sürücü: {shipmentActionData?.driverName ?? "---"}<br/>Mevcut araç: {shipmentActionData?.truckPlateNumber ?? "---"} / {shipmentActionData?.trailerPlateNumber ?? "---"}</div>

        {isLoadingShipmentVehicleOptions ? <SmallSpinner size="24px" color="#E85446" /> : (
          <div className="fields">
            <label>Sürücü
              <select value={driverId} onChange={(event) => { setDriverId(event.target.value); setValidationMessage(""); }}>
                <option value="" disabled={hasValue(initialSelectionRef.current?.driverId)}>Sürücü atanmamış</option>
                {drivers.map(driver => <option key={driver.optionId} value={driver.optionId}>{driver.optionName}{driver.isCurrent ? " (Mevcut)" : ""}</option>)}
              </select>
            </label>
            <label>Çekici
              <select value={truckId} onChange={(event) => { setTruckId(event.target.value); setValidationMessage(""); }}>
                <option value="" disabled={hasValue(initialSelectionRef.current?.truckId)}>Çekici seçin</option>
                {trucks.map(truck => (
                  <option key={truck.optionId} value={truck.optionId} disabled={truck.isBusy && !truck.isCurrent} style={truck.isBusy ? {color: "#999"} : undefined}>
                    {truck.optionName}{truck.isCurrent ? " (Mevcut)" : ""}{truck.isBusy ? " — Meşgul" : ""}
                  </option>
                ))}
              </select>
            </label>
            <label>Dorse
              <select value={trailerId} onChange={(event) => { setTrailerId(event.target.value); setValidationMessage(""); }}>
                <option value="" disabled={hasValue(initialSelectionRef.current?.trailerId)}>Dorse seçin</option>
                {trailers.map(trailer => (
                  <option key={trailer.optionId} value={trailer.optionId} disabled={trailer.isBusy && !trailer.isCurrent} style={trailer.isBusy ? {color: "#999"} : undefined}>
                    {trailer.optionName}{trailer.isCurrent ? " (Mevcut)" : ""}{trailer.isBusy ? " — Meşgul" : ""}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {(shipmentVehicleOptionsError || validationMessage) && <div className="message">{shipmentVehicleOptionsError || validationMessage}</div>}
        <div className="actions">
          <Btn width="130px" color="#E85446" bgColor="transparent" handleClick={close} isDisabled={isLoadingUpdateShipmentVehicle}>Vazgeç</Btn>
          <Btn width="160px" color="#fff" bgColor="#E85446" borderColor="transparent" handleClick={submit} isDisabled={isLoadingShipmentVehicleOptions || isLoadingUpdateShipmentVehicle || Boolean(shipmentVehicleOptionsError)}>
            {isLoadingUpdateShipmentVehicle ? <SmallSpinner size="20px" color="#fff" bgColor="#E85446" /> : "Güncelle"}
          </Btn>
        </div>
      </div>
    </Container>
  );
};

export default UpdateShipmentVehicleModal;
